import { applicationSessionStorage } from '@application/application_storage_session';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { Injectable, Scopes } from '@visiba/instantiation';
import type { RespondentType } from './screen_who_is_respondant/screen_who_is_respondant.controller';
import { FormManager } from '@feature/dynamic_form/form_manager/mod';
import { DynamicFormsApiService } from '@api_patient/dynamic_forms_api.service';
import { FormOpportunity } from '@api_patient/generated/clientv2/models/form-opportunity';
import { States } from '@api_patient/generated/clientv2/models/states';
import type { FormApiModel } from '@api_patient/generated/clientv2/models';
import { v4 } from 'uuid';
import { TimeSlotApiService } from '@api_patient/timeslot_api.service';
import { DateService } from '@application/time/date.service';
import { Dayjs } from 'dayjs';

interface BookingBasketSpec {
	useReasonForVisits: boolean;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class BookingBasketService {
	public forms: FormApiModel[] = [];
	private receptionId: number;
	private useReasonForVisits: boolean = false;
	private storage: BasketDataStorage;

	constructor(
		private readonly universalConfig: UniversalConfig,
		private readonly unitService: UnitService,
		private readonly dynamicFormsApiService: DynamicFormsApiService,
		private readonly timeSlotApiService: TimeSlotApiService,
		public readonly formManager: FormManager,
		public readonly dateService: DateService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.receptionId = unit.receptionId;

		this.storage = new BasketDataStorage({
			receptionId: this.receptionId,
		});
	}

	public load(spec: BookingBasketSpec): void {
		this.useReasonForVisits = spec.useReasonForVisits;
	}

	public async restore(): AsyncResult<void> {
		// Fetch forms if there is no reason for visit or the reason for visit is set
		if (this.get().timeSlotId && (this.useReasonForVisits === false || this.get().reasonForVisit !== null)) {
			const populateFormsResult = await this.populateForms();
			if (populateFormsResult.err) {
				return Err(populateFormsResult.err);
			}
		} else {
			this.forms = [];
			this.formManager.removeForms();
		}

		return Ok();
	}

	public clear(): void {
		this.storage.clear();
	}

	public get(): BookingBasket {
		const data = this.storage.get();

		let currentDate: Dayjs | null = null;

		if (data.currentDate && this.dateService.isValidDate(data.currentDate)) {
			currentDate = this.dateService.newDate(data.currentDate, 'YYYY-MM-DD');
		}

		return {
			...data,
			currentDate,
		};
	}

	public set(newUserData: Partial<BasketStorageData>): void {
		this.storage.set({
			...newUserData,
		});
	}

	public async unlockTimeSlot(): AsyncResult<void> {
		const { timeSlotLockId } = this.get();
		if (!timeSlotLockId) {
			return Ok();
		}

		const unlockResult = await this.timeSlotApiService.deleteLockTimeSlot(this.universalConfig.selectedUnitId, timeSlotLockId);
		if (unlockResult.err) {
			return Err(unlockResult.err);
		}

		this.set({
			timeSlotLockId: null,
		});

		return Ok();
	}

	public async updateLockedTimeSlot(timeSlotId: string): AsyncResult<void> {
		const lockResult = await this.timeSlotApiService.updateLockedTimeSlot(this.universalConfig.selectedUnitId, timeSlotId);
		if (lockResult.err) {
			return Err(lockResult.err);
		}

		this.set({
			timeSlotLockId: lockResult.ok.data.Id,
		});

		return Ok();
	}

	public async lockTimeSlot(timeSlotId: string): AsyncResult<void> {
		await this.unlockTimeSlot();

		const lockResult = await this.timeSlotApiService.lockTimeSlot(this.universalConfig.selectedUnitId, timeSlotId);
		if (lockResult.err) {
			return Err(lockResult.err);
		}

		this.set({
			timeSlotLockId: lockResult.ok.data.Id,
		});

		return Ok();
	}

	public async commit(data: CommitSpec): AsyncResult<CommitResult> {
		const validateResult = this.validate();
		if (validateResult.err) return Err(validateResult.err);

		const bookTimeSlotResult = await this.timeSlotApiService.bookTimeSlot(
			this.universalConfig.selectedUnitId,
			this.get().timeSlotId ?? '',
			{
				AsAgentFor: this.get().representativeNin ?? null,
				AsAgentForName: null,
				ConditionId: this.get().reasonForVisit,
				ExternalData: [],
				Files: data.files ?? [],
				Reason: data.reason,
				RedRobinSessionId: data.redRobinSessionId ?? null,
				TimeslotLockId: null,
			},
		);

		if (bookTimeSlotResult.err) {
			return Err(bookTimeSlotResult.err);
		}

		if (bookTimeSlotResult.ok.data.Forms.length) {
			const forms = this.formManager.getForms();
			for (const form of bookTimeSlotResult.ok.data.Forms) {
				const formWorker = forms.find((f) => f.formId === form.FormId);
				if (formWorker) {
					const answersResult = await formWorker.formDataProvider.getAnswers();
					if (answersResult.err) {
						return Err(answersResult.err);
					}

					const postAnswersResult = await this.dynamicFormsApiService.answer(
						this.universalConfig.selectedUnitId,
						form.RespondantId,
						answersResult.ok.answers,
					);

					if (postAnswersResult.err) {
						return Err(postAnswersResult.err);
					}

					const setCompletionResult = await this.dynamicFormsApiService.setCompletion(
						this.universalConfig.selectedUnitId,
						form.RespondantId,
					);

					if (setCompletionResult.err) {
						return Err(setCompletionResult.err);
					}
				}
			}
		}

		let payment = null;

		// If payment if needed
		if (
			bookTimeSlotResult.ok.data.Payment &&
			bookTimeSlotResult.ok.data.Payment.State === States.Unpaid &&
			bookTimeSlotResult.ok.data.Payment.BillId
		) {
			payment = {
				billId: bookTimeSlotResult.ok.data.Payment.BillId,
				amount: `${bookTimeSlotResult.ok.data.Price?.Formatted}`,
			};
		}

		return Ok({
			payment,
			ticket: bookTimeSlotResult.ok.data.Ticket,
		});
	}

	private validate(): Result<void> {
		if (this.useReasonForVisits && this.get().reasonForVisit == null) {
			return Err(new Error('Reason for visit is required'));
		}

		if (this.forms.length && this.formManager.getForms().find((form) => !form.getState().completed)) {
			return Err(new UnansweredFormsError('Forms are not completed'));
		}

		return Ok();
	}

	private async populateForms(): AsyncResult<void> {
		if (!this.forms.length) {
			const formsResult = await this.dynamicFormsApiService.getAllForms(
				this.universalConfig.selectedUnitId,
				this.get().reasonForVisit,
				FormOpportunity.BeforeBooking,
			);
			if (formsResult.err) {
				return Err(formsResult.err);
			}

			this.forms = formsResult.ok.data;
		}

		if (this.forms.length) {
			let formRespondantId = this.get().formRespondantId;

			if (!formRespondantId) {
				formRespondantId = v4();

				this.set({
					formRespondantId,
				});
			}

			this.formManager.addRespondants(
				this.forms
					.filter((form) => !this.formManager.getFormByFormId(form.Id))
					.map((form) => ({
						completed: false,
						editable: true,
						formId: form.Id,
						header: form.Header ?? '',
						opportunity: FormOpportunity.BeforeBooking,
						issueTrackingId: '',
						dataProvider: { type: 'local', id: formRespondantId ?? '' },
					})) ?? [],
			);
		}

		return Ok();
	}
}

interface CommitSpec {
	reason: string;
	files?: string[];
	redRobinSessionId?: string;
}

export interface CommitResult {
	payment: {
		billId: number;
		amount: string;
	} | null;
	ticket: string;
}

interface BookingBasketCommon {
	respondantType: Nullable<RespondentType>;
	representativeNin: Nullable<string>;
	reasonForVisit: Nullable<number>;
	timeSlotId: Nullable<string>;
	timeSlotLockId: Nullable<string>;
	timeSlotFilterResourceId: Nullable<number>;
	timeSlotFilterSpecialityId: Nullable<number>;
	timeSlotFilterUnitId: Nullable<number>;
	formRespondantId: Nullable<Guid>;
}

export interface BookingBasket extends BookingBasketCommon {
	currentDate: Nullable<Dayjs>;
}

export interface BasketStorageData extends BookingBasketCommon {
	currentDate: Nullable<string>;
}

interface BasketDataSpec {
	receptionId: number;
}

class BasketDataStorage {
	private receptionId: number;

	constructor(spec: BasketDataSpec) {
		this.receptionId = spec.receptionId;
	}

	public clear(): void {
		const userData = applicationSessionStorage.get('bookingBasket') ?? {};

		delete userData[this.receptionId];

		applicationSessionStorage.set('bookingBasket', userData);
	}

	public get(): BasketStorageData {
		const userData = applicationSessionStorage.get('bookingBasket') ?? {};

		return (
			userData[this.receptionId] ?? {
				respondantType: null,
				representativeNin: null,
				reasonForVisit: null,
				currentDate: null,
				timeSlotId: null,
				timeSlotLockId: null,
				timeSlotFilterResourceId: null,
				timeSlotFilterSpecialityId: null,
				timeSlotFilterUnitId: null,
				formRespondantId: null,
			}
		);
	}

	public async set(newUserData: Partial<BasketStorageData>): Promise<void> {
		const userData = applicationSessionStorage.get('bookingBasket') ?? {};

		userData[this.receptionId] = {
			...this.get(),
			...newUserData,
		};

		applicationSessionStorage.set('bookingBasket', userData);
	}
}

export class UnansweredFormsError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnansweredFormsError';
		Object.setPrototypeOf(this, UnansweredFormsError.prototype);
	}
}
